import Http from '../../network/Http'
import {ApiResponse} from '../../network/response/ApiResponse'
import {ContentType} from '../../network/Constants'
import {AssocTerm} from './model/AssocTerm'
import {Information} from '../../network/response/Information'
import {AssocOfCondominium} from './model/AssocOfCondominium'
import {AssocStatus, AssocStatusResponse, AssocStatusSearchCondition} from './model/AssocStatus'
import {ContentsForPagination} from '../shared/ContentsForPagination'

interface NewAssocTerm {
    assocTermName: string,
    assocTermStartDate: string,
    assocTermEndDate: string,
}

interface UpdateAssocTerm {
    assocTermName: string,
    assocTermStartDate: string,
    assocTermEndDate: string,
    assocTermVersion: number,
}

interface AssocTermData extends Omit<AssocTerm, 'assocTermStartDate' | 'assocTermEndDate'> {
    assocTermStartDate: string
    assocTermEndDate: string
}

export default class AssocManagementRepository {

    http: Http

    constructor(http: Http) {
        this.http = http
    }

    // 物件の管理組合一覧の取得
    async getAssocList(
        condominiumCd: string,
    ): Promise<AssocOfCondominium[]> {
        const apiResponseJSON = await this.http.get(`/api/admin/condominiums/${condominiumCd}/assocs`)
        return ApiResponse.fromArray(apiResponseJSON, AssocOfCondominium.from).details
    }

    // 期の登録
    async registerAssocTerm(
        condominiumCd: string,
        assocId: number,
        assocTermName: string,
        assocTermStartDate: string,
        assocTermEndDate: string,
    ): Promise<Information[]> {
        const requestParams: NewAssocTerm = {
            assocTermName: assocTermName,
            assocTermStartDate: assocTermStartDate,
            assocTermEndDate: assocTermEndDate,
        }

        const apiResponseJSON: ApiResponse<void> =
            await this.http.post(
                `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/terms`,
                requestParams,
                ContentType.JSON,
            )

        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON)

        return apiResponse.informations
    }

    // 期の更新
    async updateAssocTerm(
        condominiumCd: string,
        assocId: number,
        assocTermId: number,
        assocTermName: string,
        assocTermStartDate: string,
        assocTermEndDate: string,
        assocTermVersion: number,
    ): Promise<Information[]> {
        const requestParams: UpdateAssocTerm = {
            assocTermName,
            assocTermStartDate,
            assocTermEndDate,
            assocTermVersion,
        }

        const apiResponseJSON: ApiResponse<void> = await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/terms/${assocTermId}`,
            requestParams,
        )
        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON)

        return apiResponse.informations
    }

    // 期の削除
    async deleteAssocTerm(
        condominiumCd: string,
        assocId: number,
        assocTermId: number,
    ): Promise<Information> {
        const apiResponseJSON: ApiResponse<void> = await this.http.delete(
            `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/terms/${assocTermId}`,
        )

        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON)

        return apiResponse.informations[0]
    }

    // 期の一覧の取得
    async getAssocTerms(condominiumCd: string, assocId: number): Promise<AssocTerm[]> {
        const responseJson: ApiResponse<AssocTermData[]>
            = await this.http.get(`/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/terms`)

        const apiResponse: ApiResponse<AssocTerm[]> =
            ApiResponse.fromArray(responseJson, AssocTerm.from)

        return apiResponse.details
    }

    // 管理組合の登録状況の一覧の取得
    async getAssocStatusList(
        searchCondition: AssocStatusSearchCondition
    ): Promise<ContentsForPagination<AssocStatus>> {
        const responseJson: ApiResponse<ContentsForPagination<AssocStatusResponse>>
            = await this.http.post(
            `/api/admin/assoc-statuses`,
            searchCondition,
            ContentType.JSON,
        )

        const apiResponse: ApiResponse<ContentsForPagination<AssocStatus>> =
            ApiResponse.fromContentsForPagination(responseJson, AssocStatus.from)
        return apiResponse.details
    }

    getAssocStatusListCsv(): string {
        return `/api/admin/assoc-statuses/csv`
    }

    async registerAssocStatusMemo(
        condominiumCd: string,
        assocId: number,
        content: string,
    ): Promise<Information> {
        const newAssocStatusMemo = {
            content: content
        }
        const apiResponseJSON: ApiResponse<void> = await this.http.post(
             `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/assoc-status-memo`,
            newAssocStatusMemo,
            ContentType.JSON,
        )

        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON)

        return apiResponse.informations[0]
    }

    async updateAssocStatusMemo(
        condominiumCd: string,
        assocId: number,
        content: string,
        version: number
    ): Promise<Information> {
        const updateAssocStatusMemo = {
            content: content,
            version :version
        }
        const apiResponseJSON: ApiResponse<void> = await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/assoc-status-memo`,
            updateAssocStatusMemo,
        )

        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON)

        return apiResponse.informations[0]
    }
}
